import { Loader2, X, Car, Home, Sparkles } from 'lucide-react';

// ─────────────────────────────────────────
// ANALYZE MODALS
// Three related UI pieces, grouped because they all live in the
// AI-analyze flow:
//
//   1. Pending file prompt — "Analyze with AI?" banner that appears
//      right after a successful upload, asking if the agent wants
//      to extract data from the document
//   2. Analyzing modal — full-screen spinner while GPT runs
//   3. Result modal — shows what fields/autos/properties were added
// ─────────────────────────────────────────
export default function AnalyzeModals({
  pendingFile,
  onAnalyze,
  onSkipPending,
  analyzingFileId,
  analyzeData,
  onCloseResult,
}) {
  return (
    <>
      {/* 1. Pending file prompt */}
      {pendingFile && !analyzingFileId && (
        <div className="bg-purple-50 border border-purple-200 rounded-2xl px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center text-lg">✦</div>
            <div>
              <p className="text-sm font-semibold text-purple-900">Analyze with AI?</p>
              <p className="text-xs text-purple-600 mt-0.5">
                Extract policy info, vehicles, and properties from{' '}
                <span className="font-medium">{pendingFile.filename || pendingFile.name}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onAnalyze(pendingFile.id)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-purple-600 text-white hover:bg-purple-700 transition"
            >
              ✦ Analyze
            </button>
            <button
              onClick={onSkipPending}
              className="px-4 py-2 rounded-xl text-sm font-medium border border-purple-200 text-purple-700 hover:bg-purple-100 transition"
            >
              Skip
            </button>
          </div>
        </div>
      )}

      {/* 2. Analyzing modal */}
      {analyzingFileId && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-8 flex flex-col items-center gap-4 max-w-sm w-full mx-4">
            <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center">
              <Loader2 size={28} className="animate-spin text-purple-600" />
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-slate-800">Analyzing with AI</p>
              <p className="text-sm text-slate-500 mt-1">Extracting policy info, vehicles, and properties...</p>
            </div>
          </div>
        </div>
      )}

      {/* 3. Result modal */}
      {analyzeData && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
          onClick={onCloseResult}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh]"
            onClick={e => e.stopPropagation()}
          >
            <div className={`px-6 py-5 rounded-t-2xl ${analyzeData.success ? 'bg-gradient-to-r from-purple-600 to-blue-600' : 'bg-red-500'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white bg-opacity-20 flex items-center justify-center text-white text-xl">
                    {analyzeData.success ? '✦' : '✕'}
                  </div>
                  <div>
                    <p className="text-white font-bold text-base">
                      {analyzeData.success ? 'AI Extraction Complete' : 'Analysis Failed'}
                    </p>
                    <p className="text-white text-opacity-80 text-xs mt-0.5">
                      {analyzeData.success ? "Here's what was found and added to this client" : analyzeData.error}
                    </p>
                  </div>
                </div>
                <button onClick={onCloseResult} className="text-white text-opacity-70 hover:text-opacity-100 transition">
                  <X size={18} />
                </button>
              </div>
            </div>

            {analyzeData.success && (
              <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
                {Object.keys(analyzeData.fields || {}).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Fields Updated</p>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(analyzeData.fields).map(([key, val]) => (
                        <div key={key} className="bg-blue-50 rounded-xl px-3 py-2">
                          <p className="text-xs text-blue-500 font-medium capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                          <p className="text-xs font-semibold text-blue-800 truncate">{String(val)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(analyzeData.autos || []).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                      Vehicles Added ({analyzeData.autos.length})
                    </p>
                    <div className="space-y-1.5">
                      {analyzeData.autos.map((a, i) => (
                        <div key={i} className="flex items-center gap-2.5 bg-slate-50 rounded-xl px-3 py-2">
                          <Car size={13} className="text-blue-600 shrink-0" />
                          <div className="min-w-0">
                            <span className="text-xs font-semibold text-slate-700">
                              {[a.year, a.make, a.model].filter(Boolean).join(' ')}
                            </span>
                            {a.trim && (
                              <p className="text-xs text-purple-600 flex items-center gap-1 mt-0.5">
                                <Sparkles size={9} />{a.trim}
                              </p>
                            )}
                            {a.vehicleType && a.vehicleType !== 'Private Passenger' && (
                              <p className="text-xs text-amber-600 flex items-center gap-1 mt-0.5">
                                <Sparkles size={9} />{a.vehicleType}
                              </p>
                            )}
                          </div>
                          {a.vin && (
                            <span className="text-xs text-slate-400 font-mono ml-auto shrink-0">{a.vin}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(analyzeData.properties || []).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                      Properties Added ({analyzeData.properties.length})
                    </p>
                    <div className="space-y-1.5">
                      {analyzeData.properties.map((p, i) => (
                        <div key={i} className="flex items-center gap-2.5 bg-slate-50 rounded-xl px-3 py-2">
                          <Home size={13} className="text-blue-600 shrink-0" />
                          <span className="text-xs font-semibold text-slate-700">{p.address}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!Object.keys(analyzeData.fields || {}).length
                  && !(analyzeData.autos || []).length
                  && !(analyzeData.properties || []).length && (
                  <div className="text-center py-4">
                    <p className="text-sm text-slate-500">No new data could be extracted from this document.</p>
                  </div>
                )}
              </div>
            )}

            <div className="px-6 pb-5">
              <button
                onClick={onCloseResult}
                className="w-full py-2.5 rounded-xl bg-slate-800 text-white text-sm font-semibold hover:bg-slate-700 transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}